import { useEffect, useMemo, useState } from 'react';
import { FilterContextData } from 'contexts/FilterContext';
import { useContext } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import fetchProfitPoolData from 'components/pages/ProfitPoolOverview/apiUtils';
import { PROFIT_POOL_API_LIMIT } from 'components/pages/ProfitPoolOverview/apiUtils';
import { isEmpty } from 'lodash';
import { getWaterFallChartData } from 'components/pages/GrowthMarginDrivers/hooks/growthMarginDataUtils';

const useGrowthMarginBridgesData = () => {
    const selectedKPI = 'profit_growth_absolute';
    const selectedAttribute = {
        attributeType: 'Brand_Family',
        dimension: 'dim1',
        isAttribute: true,
        dataset: [],
        key: 'Brand_Family',
        dataKey: 'Brand_Family',
    };

    const [profitGrowthDriversData, setProfitGrowthDriversData] = useState<any[]>([]);
    const [min, setMin] = useState<number>(0);
    const [max, setMax] = useState<number>(0);

    // Get Previous year Data
    const {
        idForRestOfComponent,
        timePeriodSelection,
        singleSelectedTier,
        allDimAndAttributeTypeList,
        timePeriodLoading,
    } = useContext(FilterContextData);

    const {
        data: previousYearData,
        isPending: isPreviousYearDataPending,
        isError: isPreviousYearDataError,
        isSuccess: isPreviousYearDataSuccess,
    } = useQuery({
        queryKey: [
            'previous-year-profit-growth-drivers-data',
            selectedAttribute,
            singleSelectedTier,
            idForRestOfComponent,
            timePeriodSelection,
        ],
        queryFn: ({ signal }) => {
            return fetchProfitPoolData({
                selectedSubFilter: selectedAttribute,
                limit: PROFIT_POOL_API_LIMIT,
                offset: 0,
                kpi: selectedKPI,
                tierList: singleSelectedTier,
                yearAgo: true,
                groupedByList: [],
                dimIdsFilter: idForRestOfComponent,
                frequency: 'M',
                timePeriod: JSON.parse(timePeriodSelection),
                signal,
            });
        },
        enabled: Boolean(
            allDimAndAttributeTypeList &&
                'loading' in allDimAndAttributeTypeList &&
                !timePeriodLoading &&
                timePeriodSelection,
        ),
    });

    // Get Current year Data
    const {
        data: currentYearProfitData,
        isPending: isCurrentYearProfitPending,
        isError: isCurrentYearProfitError,
        isSuccess: isCurrentYearProfitSuccess,
    } = useQuery({
        queryKey: [
            'current-year-profit-growth-drivers-data',
            selectedAttribute,
            singleSelectedTier,
            idForRestOfComponent,
            timePeriodSelection,
        ],
        queryFn: ({ signal }) => {
            return fetchProfitPoolData({
                selectedSubFilter: selectedAttribute,
                limit: PROFIT_POOL_API_LIMIT,
                offset: 0,
                kpi: selectedKPI,
                tierList: singleSelectedTier,
                yearAgo: false,
                groupedByList: [],
                dimIdsFilter: idForRestOfComponent,
                frequency: 'M',
                timePeriod: JSON.parse(timePeriodSelection),
                signal,
            });
        },
        enabled: Boolean(
            allDimAndAttributeTypeList &&
                'loading' in allDimAndAttributeTypeList &&
                !timePeriodLoading &&
                timePeriodSelection,
        ),
    });

    // Get WaterFallChartData

    const {
        data: attributeProfitData,
        isPending: isAttributeProfitPending,
        isError: isAttributeProfitError,
        isSuccess: isAttributeProfitSuccess,
        fetchNextPage: fetchAttributeProfitNextPage,
        hasNextPage: hasNextAttributeProfitPage,
        isFetchingNextPage: isFetchingAttributeProfitNextPage,
    } = useInfiniteQuery({
        queryKey: [
            'profit-growth-drivers-infi',
            selectedAttribute,
            selectedKPI,
            singleSelectedTier,
            idForRestOfComponent,
            timePeriodSelection,
        ],
        queryFn: ({ pageParam, signal }) => {
            return fetchProfitPoolData({
                selectedSubFilter: selectedAttribute,
                limit: PROFIT_POOL_API_LIMIT,
                offset: pageParam as number,
                kpi: selectedKPI,
                tierList: singleSelectedTier,
                yearAgo: false,
                groupedByList: ['attribute'],
                dimIdsFilter: idForRestOfComponent,
                frequency: 'M',
                timePeriod: JSON.parse(timePeriodSelection),
                signal,
            });
        },
        getNextPageParam: (lastPage, allPages, lastPageParam) => {
            return lastPageParam + PROFIT_POOL_API_LIMIT;
        },
        initialPageParam: 0,
        enabled: Boolean(
            allDimAndAttributeTypeList &&
                'loading' in allDimAndAttributeTypeList &&
                !timePeriodLoading &&
                timePeriodSelection,
        ),
    });

    useEffect(() => {
        if (!isAttributeProfitPending && attributeProfitData?.pages) {
            const allData = attributeProfitData.pages.flatMap((page: any) => page?.data || []);

            if (!isEmpty(allData)) {
                let minProfit = Number.POSITIVE_INFINITY;
                let maxProfit = Number.NEGATIVE_INFINITY;

                const finalChartData = allData.map((item: any) => {
                    const currentProfit = item.kpi_values?.[selectedKPI] || 0;
                    if (currentProfit < minProfit) minProfit = currentProfit;
                    if (currentProfit > maxProfit) maxProfit = currentProfit;
                    return {
                        attribute_ex_id: item.attribute_ex_id,
                        attributeDesc: item.attribute_desc,
                        [selectedKPI]: currentProfit,
                        tier: item.tier,
                    };
                });

                setMin(minProfit === Number.POSITIVE_INFINITY ? 0 : minProfit);
                setMax(maxProfit === Number.NEGATIVE_INFINITY ? 0 : maxProfit);

                setProfitGrowthDriversData(finalChartData);
            } else {
                setProfitGrowthDriversData([]);
                setMin(0);
                setMax(0);
            }
        }
    }, [isAttributeProfitPending, attributeProfitData]);

    // Memoize waterfall chart data to avoid recalculating multiple times
    const waterfallChartResult = useMemo(() => {
        return getWaterFallChartData(
            profitGrowthDriversData,
            previousYearData?.data?.[0]?.kpi_values?.[selectedKPI] || 0,
            currentYearProfitData?.data?.[0]?.kpi_values?.[selectedKPI] || 0,
            selectedKPI,
        );
    }, [profitGrowthDriversData, previousYearData, currentYearProfitData, selectedKPI]);

    return {
        data: profitGrowthDriversData,
        currentYearData: currentYearProfitData?.data?.[0]?.kpi_values?.[selectedKPI] || 0,
        previousYearData: previousYearData?.data?.[0]?.kpi_values?.[selectedKPI] || 0,
        waterFallChartData: waterfallChartResult?.chartData,
        min: waterfallChartResult?.min,
        max: waterfallChartResult?.max,
        isPending: isPreviousYearDataPending || isCurrentYearProfitPending || isAttributeProfitPending,
        isError: isPreviousYearDataError || isCurrentYearProfitError || isAttributeProfitError,
        isSuccess: isPreviousYearDataSuccess && isCurrentYearProfitSuccess && isAttributeProfitSuccess,
        fetchNextPage: fetchAttributeProfitNextPage,
        hasNextPage: hasNextAttributeProfitPage,
        isFetchingNextPage: isFetchingAttributeProfitNextPage,
    };
};

export default useGrowthMarginBridgesData;
